// Formularios (módulo Formularios, Etapa 1) — vocabulario compartido entre
// server (validación, persistencia) y web (constructor de campos, render del
// formulario público). Fuente única: ningún lado retipa esta lista a mano.
export const FORM_FIELD_TYPES = [
    'text', 'email', 'phone', 'number', 'select', 'multiselect', 'date', 'textarea', 'checkbox',
];
export const FORM_FIELD_TYPES_WITH_OPTIONS = new Set(['select', 'multiselect']);
// Casilla única de sí/no — el caso típico es "acepto los términos y
// condiciones". No lleva opciones: la etiqueta ES la afirmación que se acepta.
//
// Para este tipo, `placeholder` se usa como TEXTO DE AYUDA debajo de la
// casilla, no como texto dentro del campo (una casilla no tiene dónde poner un
// placeholder). Así una etiqueta corta y clicable — "Acepto los términos y
// condiciones" — puede convivir con el párrafo legal largo que la acompaña,
// que va abajo en gris. Sin esto, formularios como los de Go High Level
// importan el consentimiento entero como etiqueta y queda ilegible.
export const FORM_FIELD_TYPES_WITH_HELP_TEXT = new Set(['checkbox']);
// Ancho del campo en el formulario público. 'half' = media columna: dos
// campos 'half' consecutivos caen lado a lado (nombre/apellido). En móvil
// TODO pasa a una columna, sin excepción — ver .fields-grid en
// routes/public-forms.ts.
export const FORM_FIELD_WIDTHS = ['full', 'half'];
// Tema del formulario público. Los tres campos son NULL-able en DB: null =
// "no configurado" y se resuelve a estos defaults, que son exactamente los
// valores hardcodeados que el formulario tenía antes de ser personalizable —
// un formulario nuevo o preexistente sin tema se ve idéntico a siempre.
export const FORM_THEME_DEFAULTS = {
    primary: '#0ea5e9',
    text: '#1e293b',
    /** Fondo por defecto: el blanco de siempre. null en la fila = este valor, así
     *  que ningún formulario existente cambia de aspecto. */
    bg: '#ffffff',
    /** Fondo de la página que rodea a la tarjeta. El azulado grisáceo de
     *  siempre. */
    page: '#f1f5f9',
};
/** Fondo transparente: la card no pinta nada y se ve el fondo de la página
 *  donde está incrustado el formulario. Es el caso que más sirve para embeber,
 *  y por eso es un valor propio y no un hex. */
export const FORM_BG_TRANSPARENT = 'transparent';
/** ¿Qué se guardó en themeBg? null = blanco por defecto. */
export function isTransparentBg(themeBg) {
    return themeBg === FORM_BG_TRANSPARENT;
}
/** El fondo de la TARJETA como hex, o null si es transparente (no hay color
 *  contra el cual medir nada). Es la capa sobre la que va el texto, así que es
 *  contra esta que se mide el contraste — nunca contra la de alrededor. */
export function resolveFormBg(themeBg) {
    if (isTransparentBg(themeBg))
        return null;
    return themeBg && FORM_HEX_COLOR_RE.test(themeBg) ? themeBg : FORM_THEME_DEFAULTS.bg;
}
/** El fondo de la PÁGINA (lo que rodea a la tarjeta) como hex, o null si es
 *  transparente. Nada de texto va sobre esta capa: no participa del contraste. */
export function resolveFormPageBg(themePageBg) {
    if (isTransparentBg(themePageBg))
        return null;
    return themePageBg && FORM_HEX_COLOR_RE.test(themePageBg) ? themePageBg : FORM_THEME_DEFAULTS.page;
}
export const FORM_HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
// ── Contraste (aviso, nunca bloqueo) ──────────────────────────────────────────
// El color de texto se mide contra el FONDO ELEGIDO, no contra un blanco fijo
// (2026-08-19): con fondo propio, medir contra blanco avisaba mal — decía que
// un texto claro estaba mal cuando el fondo era oscuro, y callaba cuando de
// verdad era ilegible. Si el contraste queda por debajo del piso WCAG AA el
// panel avisa, pero deja guardar: es la marca del cliente, no una regla del
// sistema.
//
// Con fondo TRANSPARENTE no hay nada contra qué medir: el fondo lo pone la
// página donde se incruste, que no conocemos. Ahí no se calcula un número
// falso, se dice que no se puede saber.
/** @deprecated Usar resolveFormBg(form.themeBg). Se mantiene para los llamados
 *  que todavía asumen la card blanca. */
export const FORM_CARD_BG = '#ffffff';
export const FORM_CONTRAST_AA = 4.5;
function hexToRgb(hex) {
    if (!FORM_HEX_COLOR_RE.test(hex))
        return null;
    let h = hex.slice(1);
    if (h.length === 3)
        h = h.split('').map(c => c + c).join('');
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ];
}
// Luminancia relativa WCAG 2.1 (fórmula tal cual la define la spec).
function relativeLuminance([r, g, b]) {
    const channel = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
// Ratio de contraste WCAG entre dos hex. null si alguno no es un hex válido.
export function contrastRatio(hexA, hexB) {
    const a = hexToRgb(hexA);
    const b = hexToRgb(hexB);
    if (!a || !b)
        return null;
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const light = Math.max(la, lb);
    const dark = Math.min(la, lb);
    return (light + 0.05) / (dark + 0.05);
}
//# sourceMappingURL=forms.js.map